import { Button, Card, Chip, ListRow, Pill, Switch } from '@gramas/ui'
import { useEffect, useState } from 'react'
import {
  DEFAULT_PREFERENCES,
  disableReminders,
  enableReminders,
  type ReminderPreferences,
  readSubscriptionState,
  type SubscriptionState,
  sendTestReminder,
  updatePreferences,
} from '../push/reminders.js'
import { showErrorToast } from '../shell/toastStore.js'

const HOURS = Array.from({ length: 24 }, (_, i) => i)

/** "20:00" — the only format a 24-hour country reads without thinking. */
const clock = (hour: number): string => `${String(hour).padStart(2, '0')}:00`

/**
 * The reminder switches.
 *
 * One master switch, because permission is a single yes-or-no that the browser
 * will only ask about once ever; the three reminders underneath are ordinary
 * settings that need no permission of their own. Turning the master off
 * unsubscribes rather than merely muting — a notification permission left
 * granted for an app that never sends anything is a small dishonesty.
 *
 * The test button exists because "no reminder arrived" is not evidence of
 * anything, and the first real one is hours away.
 */
export function RemindersCard() {
  const [state, setState] = useState<SubscriptionState | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    void readSubscriptionState().then(setState)
  }, [])

  if (!state) return null

  const on = state.endpoint != null && state.permission === 'granted'

  const toggle = async (next: boolean) => {
    setBusy(true)
    setMessage(null)
    try {
      if (!next) {
        await disableReminders()
        setState(await readSubscriptionState())
        return
      }
      const result = await enableReminders(state.preferences)
      setMessage(
        result === 'enabled'
          ? null
          : result === 'denied'
            ? 'Pranešimai uždrausti naršyklės nustatymuose — įjungti galima tik ten.'
            : result === 'server-unconfigured'
              ? 'Serveryje nesukonfigūruoti VAPID raktai.'
              : 'Nepavyko įjungti priminimų.',
      )
      setState(await readSubscriptionState())
    } finally {
      setBusy(false)
    }
  }

  /**
   * Moves the switch first and asks the server second, because a toggle that
   * waits on a round trip reads as a toggle that did not register. If the
   * server refuses it goes back where it was and says so — silently keeping the
   * new position is how a setting comes to be "on" here and off everywhere else.
   */
  const change = async (changes: Partial<ReminderPreferences>) => {
    const previous = state.preferences
    setState((s) => (s ? { ...s, preferences: { ...s.preferences, ...changes } } : s))
    try {
      await updatePreferences(changes)
    } catch {
      setState((s) => (s ? { ...s, preferences: previous } : s))
      showErrorToast('Nepavyko išsaugoti priminimo — reikia ryšio.')
    }
  }

  if (state.support === 'needs-install') {
    return (
      <Card className="mx-4 px-3 py-3">
        <p className="text-sm text-mute">
          „iOS“ leidžia pranešimus tik iš pradžios ekrane įdiegtos programėlės. Įdiekite „Gramą“ per
          Bendrinti → Įtraukti į pradžios ekraną, tada grįžkite čia.
        </p>
      </Card>
    )
  }

  if (state.support === 'unsupported') {
    return (
      <Card className="mx-4 px-3 py-3">
        <p className="text-sm text-mute">Ši naršyklė pranešimų nepalaiko.</p>
      </Card>
    )
  }

  const preferences = state.preferences ?? DEFAULT_PREFERENCES

  return (
    <Card className="mx-4">
      {/* The switch is disabled rather than inert when the server has no keys:
          it used to swallow the tap and print the reason at the bottom of the
          card, which from the thumb's point of view is a broken toggle. */}
      <ListRow
        title="Priminimai"
        subtitle={
          !state.serverReady
            ? 'Serveryje nesukonfigūruoti VAPID raktai'
            : busy
              ? 'Jungiama…'
              : on
                ? 'Įjungti šiame įrenginyje'
                : 'Išjungti'
        }
        last={!on}
        accessory={
          <Switch
            label="Priminimai"
            checked={on}
            disabled={busy || !state.serverReady}
            onCheckedChange={(next) => void toggle(next)}
          />
        }
      />

      {on && (
        <>
          <ListRow
            title="Vakarinis priminimas"
            subtitle={`${clock(preferences.eveningHour)} · tik jei diena tuščia`}
            accessory={
              <Switch
                label="Vakarinis priminimas"
                checked={preferences.eveningEnabled}
                onCheckedChange={(next) => void change({ eveningEnabled: next })}
              />
            }
          />
          {preferences.eveningEnabled && (
            <HourRow
              label="Vakaro laikas"
              value={preferences.eveningHour}
              from={16}
              to={23}
              onChange={(hour) => void change({ eveningHour: hour })}
            />
          )}

          <ListRow
            title="Rytinis svėrimas"
            subtitle={`${clock(preferences.morningHour)} · tik jei nepasvėrėte`}
            accessory={
              <Switch
                label="Rytinis svėrimas"
                checked={preferences.weighInEnabled}
                onCheckedChange={(next) => void change({ weighInEnabled: next })}
              />
            }
          />
          {preferences.weighInEnabled && (
            <HourRow
              label="Ryto laikas"
              value={preferences.morningHour}
              from={5}
              to={12}
              onChange={(hour) => void change({ morningHour: hour })}
            />
          )}

          <ListRow
            title="Savaitės suvestinė"
            subtitle="Sekmadieniais 19:00"
            accessory={
              <Switch
                label="Savaitės suvestinė"
                checked={preferences.weeklyEnabled}
                onCheckedChange={(next) => void change({ weeklyEnabled: next })}
              />
            }
          />

          <ListRow
            title="Rytinė suvestinė"
            subtitle={`${clock(preferences.briefHour)} · išjungta pagal nutylėjimą`}
            accessory={
              <Switch
                label="Rytinė suvestinė"
                checked={preferences.morningBriefEnabled}
                onCheckedChange={(next) => void change({ morningBriefEnabled: next })}
              />
            }
          />
          {preferences.morningBriefEnabled && (
            <HourRow
              label="Rytinės suvestinės laikas"
              value={preferences.briefHour}
              from={5}
              to={12}
              onChange={(hour) => void change({ briefHour: hour })}
            />
          )}

          <div className="flex items-center gap-2 px-3 py-3">
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                setBusy(true)
                void sendTestReminder()
                  .then((ok) =>
                    setMessage(
                      ok ? 'Išsiųsta — turėtų atkeliauti tuoj pat.' : 'Nepavyko išsiųsti.',
                    ),
                  )
                  .finally(() => setBusy(false))
              }}
            >
              Išbandyti
            </Button>
            {message && <span className="text-sm text-mute">{message}</span>}
          </div>
        </>
      )}

      {!on && message && (
        <div className="px-3 pb-3">
          <Chip tone="warn">{message}</Chip>
        </div>
      )}
    </Card>
  )
}

/** A row of hours, because a native time picker on a fixed :00 is overkill. */
function HourRow({
  label,
  value,
  from,
  to,
  onChange,
}: {
  label: string
  value: number
  from: number
  to: number
  onChange: (hour: number) => void
}) {
  return (
    <div className="flex flex-col gap-1.5 border-b border-line px-3 py-3">
      <span className="text-xs text-mute">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {HOURS.filter((h) => h >= from && h <= to).map((hour) => (
          <Pill key={hour} numeric active={hour === value} onClick={() => onChange(hour)}>
            {clock(hour)}
          </Pill>
        ))}
      </div>
    </div>
  )
}
